import React, { useState, useEffect, useRef } from "react";
import { FaUserAlt, FaPen } from "react-icons/fa";
import { IoMdClose } from "react-icons/io";
import { GoVerified, GoUnverified } from "react-icons/go";
import { useUserStore } from "../../../store/useUser";
import { useAdminUsersId, useUserStatus } from "../../../sevices/hooks/admin";
import { TbEdit } from "react-icons/tb";
import { toast } from "sonner";
import _ from "lodash";

export interface UserModalProps {
  isOpen: boolean;
  onClose: (shouldShow: boolean) => void;
  refetchTable: any;
}

// Helper function to map API status to UI status
const mapStatusToUIStatus = (
  apiStatus: string
): "Active" | "Pending" | "Suspended" | "Blocked" | "Deleted" => {
  // Normalize the status string to lowercase for case-insensitive comparison
  const normalizedStatus = apiStatus?.toLowerCase() || "";

  // Map verified to Active
  if (normalizedStatus === "verified") {
    return "Active";
  }

  // Map other statuses - capitalize first letter for consistency
  if (
    ["active", "pending", "suspended", "blocked", "deleted"].includes(
      normalizedStatus
    )
  ) {
    return (normalizedStatus.charAt(0).toUpperCase() +
      normalizedStatus.slice(1)) as any;
  }

  // Default fallback
  return "Active";
};

const getStatusColor = (status: string): string => {
  const statusMap: Record<string, string> = {
    Active: "#0eb683",
    Verified: "#0eb683",
    Pending: "#F59E0B",
    Suspended: "#EF4444",
    Blocked: "#991B1B",
    Deleted: "#6B7280",
  };

  return statusMap[status] || "#0eb683";
};

const UsersModal: React.FC<UserModalProps> = ({
  isOpen,
  onClose,
  refetchTable,
}) => {
  const usersId = useUserStore((state) => state.selectedUsersId);
  const updateStatus = useUserStatus();
  const [statusLoading, setStatusLoading] = useState(false);
  const [isEditingStatus, setIsEditingStatus] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState<
    "Active" | "Pending" | "Suspended" | "Blocked" | "Deleted"
  >("Active");
  const [statusChangeReason, setStatusChangeReason] = useState("");
  const [currentUserData, setCurrentUserData] = useState<any>(null);
  const [showSkeleton, setShowSkeleton] = useState(true);
  const { data: userIdData, refetch } = useAdminUsersId(usersId as string);
  const overLayRef = useRef(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const editButtonRef = useRef<HTMLButtonElement>(null);

  // Store the current user ID to detect changes
  const [prevUserId, setPrevUserId] = useState<string | null>(null);

  // Always show skeleton when user ID changes
  useEffect(() => {
    if (usersId !== prevUserId) {
      // Clear current data and show skeleton when user changes
      setCurrentUserData(null);
      setShowSkeleton(true);
      setPrevUserId(usersId as string);
    }
  }, [usersId, prevUserId]);

  // Update current user data only when new data is available and matches current user ID
  useEffect(() => {
    const fetchData = async () => {
      if (usersId) {
        setShowSkeleton(true);
        const result = await refetch();
        if (result.data && usersId === prevUserId) {
          const userData = result.data.data.data;
          setCurrentUserData(userData);

          // Map API status to UI status and set it
          if (userData?.profileStatus) {
            setSelectedStatus(mapStatusToUIStatus(userData.profileStatus));
          }

          setShowSkeleton(false);
        }
      }
    };

    fetchData();
  }, [usersId, refetch, prevUserId]);

  // Reset editing state when user changes
  useEffect(() => {
    setIsEditingStatus(false);
    if (currentUserData?.profileStatus) {
      setSelectedStatus(mapStatusToUIStatus(currentUserData.profileStatus));
    }
  }, [currentUserData]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        isEditingStatus &&
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        editButtonRef.current &&
        !editButtonRef.current.contains(event.target as Node)
      ) {
        setIsEditingStatus(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isEditingStatus]);

  const handleOverLayClick = (e: React.MouseEvent<HTMLElement, MouseEvent>) => {
    e.target === overLayRef.current && onClose(false);
  };

  const mapUIStatusToAPIStatus = (uiStatus: string): string => {
    return uiStatus;
  };

  const handleStatusChange = async () => {
    setStatusLoading(true);
    try {
      // Convert UI status to API status if needed
      const apiStatus = mapUIStatusToAPIStatus(selectedStatus);

      console.log(apiStatus, "apiStatus");

      const res = await updateStatus.mutateAsync({
        userId: usersId as string,
        status: apiStatus,
      });

      // Update the current user data with the new status
      setCurrentUserData((prev: any) => {
        if (prev) {
          return {
            ...prev,
            profileStatus: apiStatus,
          };
        }
        return prev;
      });

      console.log(res, "resres");
      refetch();
      refetchTable();
      if (res?.data?.status === "success") {
        toast("Status updated successfully");
        setStatusLoading(false);
        setIsEditingStatus(false);
      }
    } catch (error) {
      //@ts-ignore
      toast(error?.response?.data?.message);
      setIsEditingStatus(false);
      setStatusLoading(false);
    }
  };

  // Skeleton loader component
  const SkeletonLoader = () => (
    <div className="animate-pulse">
      <div className="flex flex-col items-center justify-center">
        <div className="rounded-full w-16 h-16 bg-gray-200 mt-16"></div>
        <div className="mt-4 w-48 h-6 bg-gray-200 rounded"></div>
        <div className="mt-2 w-32 h-4 bg-gray-200 rounded"></div>
      </div>
      <div className="mx-4 mt-8">
        {[1, 2, 3, 4, 5].map((item) => (
          <div key={item} className="flex items-center justify-between mt-4">
            <div className="w-32 h-4 bg-gray-200 rounded"></div>
            <div className="w-40 h-4 bg-gray-200 rounded"></div>
          </div>
        ))}
      </div>
    </div>
  );

  if (!isOpen) return null;

  return (
    <div className="modal fixed w-full h-full top-0 left-0 justify-center items-center transition-opacity duration-300 z-50">
      <div
        ref={overLayRef}
        className="modal_overlay absolute flex justify-end top-0 right-0 w-full h-full cursor-pointer bg-[#13111173]"
        onClick={handleOverLayClick}
      >
        <div className="modal_box relative bg-[#fff] h-full w-[450px] rounded-l-lg overflow-auto">
          <div
            onClick={() => onClose(false)}
            className="close absolute top-3 right-3 flex items-center justify-center rounded-full
            bg-[#F7FBFB] p-3 hover:rounded-full hover:text-center z-10"
          >
            <span className="hover:rotate-90 hover:transform transition duration-150 ease-in-out">
              <IoMdClose size={29} />
            </span>
          </div>

          {showSkeleton || !currentUserData ? (
            <SkeletonLoader />
          ) : (
            <>
              <div className="flex flex-col items-center justify-center">
                <div className="cursor-pointer active:scale-90 ease-in-out transition-all duration-100">
                  <div className="rounded-full w-16 h-16 border-2 border-gray-400 mt-16 flex items-center justify-center">
                    <span className="text-center rounded-full w-14 h-14 items-center flex justify-center text-white bg-gray-400">
                      <FaUserAlt size={40} />
                    </span>
                  </div>
                </div>
                <div className="mt-4">
                  <h1 className="text-center font-bold text-2xl text-[#2B2B2B]">
                    {currentUserData?.firstName} {currentUserData?.lastName}
                  </h1>
                  <h3 className="mt-2 font-normal text-sm text-gray-400">
                    User ID -{" "}
                    <span className="text-[#0eb683]">
                      {currentUserData?.id}
                    </span>
                  </h3>
                </div>
              </div>
              <div className="mx-4">
                <div className="flex items-center justify-between gap-1 mt-4">
                  <h1 className="text-sm font-normal text-gray-400">
                    Phone Number
                  </h1>
                  <h1 className="text-sm font-normal text-[#2B2B2B]">
                    {currentUserData?.phoneNumber}
                  </h1>
                </div>
                <div className="flex items-center justify-between gap-1 mt-4">
                  <h1 className="text-sm font-normal text-gray-400">
                    Email Address
                  </h1>
                  <h1 className="text-sm font-normal text-[#2B2B2B]">
                    {currentUserData?.email}
                  </h1>
                </div>
                <div className="flex items-center justify-between gap-1 mt-4">
                  <h1 className="text-sm font-normal text-gray-400">
                    Last Login
                  </h1>
                  <h1 className="text-sm font-normal text-[#2B2B2B]">
                    35 secs ago
                  </h1>
                </div>
                <div className="flex items-center justify-between gap-1 mt-4">
                  <h1 className="text-sm font-normal text-gray-400">
                    Referred By
                  </h1>
                  <h1 className="text-[#0eb683] text-sm font-normal underline">
                    {currentUserData?.refereeCode}
                  </h1>
                </div>
                <div className="flex items-center justify-between gap-1 mt-4">
                  <h1 className="text-sm font-normal text-gray-400">
                    KYC Status
                  </h1>
                  <div className="flex items-center gap-2">
                    <h1 className="text-[#2B2B2B] text-sm font-normal">
                      {currentUserData?.kycStatus ?? "Unverified"}
                    </h1>
                    <span>
                      {currentUserData?.kycStatus ? (
                        <span className="text-[#0eb683] text-sm">
                          <GoVerified size={20} />
                        </span>
                      ) : (
                        <span className="text-[#a10] text-sm">
                          <GoUnverified size={20} />
                        </span>
                      )}
                    </span>
                  </div>
                </div>
                <div className="flex items-center justify-between gap-1 mt-4 pb-7 relative">
                  <div className="flex items-center">
                    <h1 className="text-sm font-normal text-gray-400">
                      Profile Status
                    </h1>
                    <button
                      ref={editButtonRef}
                      onClick={() => setIsEditingStatus(!isEditingStatus)}
                      className="p-1 hover:bg-gray-100 rounded-full text-black-500 hover:text-gray-700 transition-all duration-200 ease-in-out cursor-pointer "
                    >
                      <TbEdit size={22} />
                      {/* <FaPen size={12} /> */}
                    </button>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="flex items-center gap-2">
                      <div
                        className="flex items-center gap-1"
                        style={{
                          color: getStatusColor(currentUserData?.profileStatus),
                        }}
                      >
                        <h1 className="text-sm font-medium">
                          {_.startCase(currentUserData?.profileStatus)}
                        </h1>
                        <span>
                          {currentUserData?.profileStatus?.toLowerCase() !==
                          "pending" ? (
                            <GoVerified size={18} />
                          ) : (
                            <GoUnverified size={18} />
                          )}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Animated dropdown */}
                  {isEditingStatus && (
                    <div
                      ref={dropdownRef}
                      className="absolute right-0 top-10 z-20 bg-white rounded-lg shadow-lg border border-gray-100 w-80 animate-fadeIn"
                      style={{
                        transformOrigin: "top right",
                        animation: "fadeInScale 0.2s ease-out forwards",
                      }}
                    >
                      <div className="p-3">
                        <h3 className="text-sm font-medium text-gray-700 mb-2">
                          Update Profile Status
                        </h3>
                        <div className="space-y-2 mb-3">
                          {[
                            "Active",
                            "Pending",
                            "Suspended",
                            "Blocked",
                            "Deleted",
                          ].map((status) => (
                            <div
                              key={status}
                              className={`flex items-center gap-2 p-2 rounded-md cursor-pointer transition-all duration-150 hover:bg-gray-50 ${
                                selectedStatus === status ? "bg-gray-50" : ""
                              }`}
                              onClick={() => setSelectedStatus(status as any)}
                            >
                              <div
                                className={`w-3 h-3 rounded-full`}
                                style={{
                                  backgroundColor: getStatusColor(status),
                                }}
                              ></div>
                              <div className="flex-1 text-sm">{status}</div>
                              {selectedStatus === status && (
                                <div className="w-4 h-4 flex items-center justify-center">
                                  <div className="w-2 h-2 bg-gray-600 rounded-full"></div>
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                        <textarea
                          className="w-full text-sm p-2 border border-gray-200 rounded-md focus:outline-none focus:ring-1 focus:ring-[#0eb683] placeholder:text-xs h-20 mb-3"
                          value={statusChangeReason}
                          onChange={(e) =>
                            setStatusChangeReason(e.target.value)
                          }
                          placeholder="Reason for status change (optional)"
                        ></textarea>
                        <div className="flex gap-2">
                          <button
                            className="flex-1 bg-[#0eb683] text-white px-3 py-2 text-sm rounded-md hover:bg-[#0c9b6d] transition-all"
                            onClick={handleStatusChange}
                            disabled={statusLoading}
                          >
                            {statusLoading ? "Updating..." : "Update"}
                          </button>
                          <button
                            className="flex-1 border border-gray-300 text-gray-600 px-3 py-2 text-sm rounded-md hover:bg-gray-50 transition-all"
                            onClick={() => setIsEditingStatus(false)}
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

// Add these styles to your global CSS or component
const globalStyles = `
@keyframes fadeInScale {
  from {
    opacity: 0;
    transform: scale(0.95);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
}

.animate-fadeIn {
  animation: fadeInScale 0.2s ease-out forwards;
}
`;

export default UsersModal;
